import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import ParticlesBackground from '../components/ParticlesBackground';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaChevronRight } from 'react-icons/fa';

// Advanced animations
const glowPulse = keyframes`
  0% { text-shadow: 0 0 10px rgba(100, 182, 255, 0.5), 0 0 20px rgba(100, 182, 255, 0.3); }
  50% { text-shadow: 0 0 15px rgba(100, 182, 255, 0.8), 0 0 30px rgba(100, 182, 255, 0.5), 0 0 40px rgba(0, 255, 255, 0.3); }
  100% { text-shadow: 0 0 10px rgba(100, 182, 255, 0.5), 0 0 20px rgba(100, 182, 255, 0.3); }
`;

const scanLine = keyframes`
  0% { transform: translateY(-100%); }
  100% { transform: translateY(100%); }
`;

const dataFlowAnimation = keyframes`
  0% { background-position: 0% 0%; }
  100% { background-position: 200% 0%; }
`;

const techGridPulse = keyframes`
  0% { opacity: 0.2; }
  50% { opacity: 0.4; }
  100% { opacity: 0.2; }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const rotateGlow = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Styled Components
const NumberWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00ffff;
  background: rgba(0, 20, 40, 0.6);
  border-radius: 8px;
  box-shadow: 0 0 15px rgba(0, 255, 255, 0.3);
  border: 1px solid rgba(0, 255, 255, 0.3);
  font-family: 'Orbitron', sans-serif;
  font-size: 16px;
  font-weight: bold;
  
  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 8px;
    background: linear-gradient(45deg, transparent, rgba(0, 255, 255, 0.3), transparent);
    animation: ${rotateGlow} 3s linear infinite;
    opacity: 0.5;
    z-index: -1;
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: 1px;
    border-radius: 7px;
    background: linear-gradient(135deg, rgba(0, 150, 255, 0.1), transparent);
    z-index: -1;
  }
`;

const BemVindoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  color: #fff;
  text-align: center;
  position: relative;
  overflow: hidden;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 100%),
      repeating-linear-gradient(45deg, rgba(6, 78, 141, 0.1) 0px, rgba(6, 78, 141, 0.1) 1px, transparent 1px, transparent 10px),
      repeating-linear-gradient(135deg, rgba(6, 78, 141, 0.1) 0px, rgba(6, 78, 141, 0.1) 1px, transparent 1px, transparent 10px);
    z-index: -1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100vh;
    background: linear-gradient(0deg, rgba(0,0,0,0) 0%, rgba(0,150,255,0.1) 50%, rgba(0,0,0,0) 100%);
    animation: ${scanLine} 8s linear infinite;
    z-index: -1;
    pointer-events: none;
  }

  h1 {
    font-family: 'Orbitron', sans-serif;
    font-size: 3.5rem;
    margin-bottom: 2rem;
    background: linear-gradient(90deg, rgba(140, 202, 255, 0.95), rgba(92, 227, 255, 0.95), rgba(70, 255, 255, 0.95));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: ${glowPulse} 3s infinite;
    letter-spacing: 2px;
    position: relative;
    text-transform: uppercase;
    font-weight: 700;
    filter: drop-shadow(0 0 10px rgba(0, 150, 255, 0.5));
    
    &::after {
      content: '';
      position: absolute;
      bottom: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 150px;
      height: 4px;
      background: linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.8), transparent);
      border-radius: 2px;
      animation: ${glowPulse} 3s infinite;
      box-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
    }
  }

  h2 {
    font-family: 'Orbitron', sans-serif;
    font-size: 1.8rem;
    margin: 0.5rem 0 2.5rem;
    color: #64B6FF;
    position: relative;
    animation: ${fadeIn} 1s ease-out forwards;
    letter-spacing: 1px;
    text-shadow: 0 0 10px rgba(100, 182, 255, 0.5);
    display: inline-block;
    padding: 0.5rem 2rem;
    background: rgba(0, 20, 40, 0.4);
    border-radius: 30px;
    border: 1px solid rgba(0, 150, 255, 0.2);
    
    &::before, &::after {
      content: '';
      position: absolute;
      height: 2px;
      background: linear-gradient(90deg, transparent, rgba(0, 150, 255, 0.5), transparent);
      width: 200px;
      animation: ${dataFlowAnimation} 3s linear infinite;
    }
    
    &::before {
      top: 50%;
      right: 100%;
      margin-right: 20px;
    }
    
    &::after {
      top: 50%;
      left: 100%;
      margin-left: 20px;
    }
    
    // Connecting lines to the list
    &::before, &::after {
      content: '';
      position: absolute;
      width: 2px;
      height: 40px;
      background: linear-gradient(180deg, rgba(0, 150, 255, 0.5), transparent);
      left: 50%;
      transform: translateX(-50%);
    }
    
    &::before {
      top: -40px;
    }
    
    &::after {
      bottom: -40px;
    }
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 1rem 0 3rem;
    max-width: 800px;
    width: 100%;
    position: relative;
    background: rgba(0, 20, 40, 0.3);
    border-radius: 20px;
    padding: 2rem;
    backdrop-filter: blur(10px);
    box-shadow: 
      0 0 30px rgba(0, 150, 255, 0.15),
      inset 0 0 20px rgba(0, 150, 255, 0.1);
    border: 1px solid rgba(0, 150, 255, 0.2);
    overflow: hidden;
    transform-style: preserve-3d;
    perspective: 1000px;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: 
        linear-gradient(90deg, transparent 0%, rgba(0, 150, 255, 0.1) 50%, transparent 100%),
        linear-gradient(180deg, rgba(0, 150, 255, 0.1) 0%, transparent 100%);
      z-index: -1;
      opacity: 0.5;
      animation: ${dataFlowAnimation} 8s linear infinite;
    }
    
    &::after {
      content: '';
      position: absolute;
      inset: 1px;
      background: 
        linear-gradient(90deg, transparent 0%, rgba(0, 150, 255, 0.1) 50%, transparent 100%),
        repeating-linear-gradient(45deg, rgba(6, 78, 141, 0.1) 0px, rgba(6, 78, 141, 0.1) 1px, transparent 1px, transparent 10px);
      border-radius: 20px;
      z-index: -1;
      opacity: 0.3;
      animation: ${techGridPulse} 4s infinite;
    }
  }

  li {
    margin: 1.5rem 0;
    font-size: 1.2rem;
    position: relative;
    padding: 1rem 1rem 1rem 4rem;
    background: rgba(0, 15, 30, 0.4);
    border-radius: 12px;
    transition: all 0.3s ease;
    animation: ${fadeIn} 0.5s ease-out forwards;
    animation-delay: calc(0.2s * var(--index, 0));
    opacity: 0;
    transform: translateY(20px);
    border-left: 2px solid rgba(0, 255, 255, 0.5);
    text-align: left;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    
    // Linha de conexão entre os itens
    &:not(:last-child)::after {
      content: '';
      position: absolute;
      left: 27px;
      bottom: -1.5rem;
      width: 2px;
      height: 1.5rem;
      background: linear-gradient(180deg, rgba(0, 255, 255, 0.5), transparent);
      opacity: 0.5;
    }
    
    &:hover {
      transform: translateX(5px);
      background: rgba(0, 20, 40, 0.6);
      box-shadow: 
        0 8px 20px rgba(0, 0, 0, 0.3),
        0 0 15px rgba(0, 150, 255, 0.2);
      border-left: 2px solid rgba(0, 255, 255, 0.8);
      
      ${NumberWrapper} {
        background: rgba(0, 30, 60, 0.8);
        box-shadow: 
          0 0 20px rgba(0, 255, 255, 0.4),
          inset 0 0 10px rgba(0, 255, 255, 0.2);
      }
    }
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(0, 150, 255, 0.05), transparent);
      background-size: 200% 100%;
      animation: ${dataFlowAnimation} 3s linear infinite;
      z-index: -1;
      border-radius: 12px;
      opacity: 0.8;
    }
  }

  p {
    max-width: 800px;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
    animation: ${fadeIn} 1s ease-out forwards;
    animation-delay: 1s;
    opacity: 0;
  }
`;

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 20px rgba(0, 255, 255, 0.2), 0 0 30px rgba(0, 255, 255, 0.1); }
  50% { box-shadow: 0 0 25px rgba(0, 255, 255, 0.3), 0 0 40px rgba(0, 255, 255, 0.2); }
  100% { box-shadow: 0 0 20px rgba(0, 255, 255, 0.2), 0 0 30px rgba(0, 255, 255, 0.1); }
`;

const clientGradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const glitchEffect = keyframes`
  0% {
    text-shadow: 0.05em 0 0 rgba(0, 255, 255, 0.75), -0.05em -0.025em 0 rgba(100, 182, 255, 0.75);
    transform: translate(0.025em, 0.0125em);
  }
  15% {
    text-shadow: -0.05em -0.025em 0 rgba(0, 255, 255, 0.75), 0.025em 0.025em 0 rgba(100, 182, 255, 0.75);
    transform: translate(-0.025em, 0em);
  }
  50% {
    text-shadow: 0.05em 0.05em 0 rgba(0, 255, 255, 0.75), 0.05em 0 0 rgba(100, 182, 255, 0.75);
    transform: translate(0em, 0em);
  }
  85% {
    text-shadow: 0.05em 0 0 rgba(0, 255, 255, 0.75), -0.05em 0 0 rgba(100, 182, 255, 0.75);
    transform: translate(0.0125em, -0.025em);
  }
  100% {
    text-shadow: -0.05em 0 0 rgba(0, 255, 255, 0.75), -0.025em -0.025em 0 rgba(100, 182, 255, 0.75);
    transform: translate(-0.025em, -0.0125em);
  }
`;

const scanlineAnim = keyframes`
  0% { transform: translateY(-100%); }
  100% { transform: translateY(100%); }
`;

const borderFlash = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
`;

const textReveal = keyframes`
  0% { clip-path: polygon(0 0, 0 0, 0 100%, 0 100%); }
  100% { clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%); }
`;

const ClientNameContainer = styled.div`
  position: relative;
  margin: 0.5rem 0 2.5rem;
  padding: 1rem;
  width: auto;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  transform-style: preserve-3d;
  perspective: 1000px;
`;

const ClientNameCorner = styled.div`
  position: absolute;
  width: 15px;
  height: 15px;
  border-color: rgba(0, 255, 255, 0.8);
  z-index: 1;
  opacity: 0.8;
  animation: ${borderFlash} 3s infinite;
  
  &.top-left {
    top: 0;
    left: 0;
    border-top: 2px solid;
    border-left: 2px solid;
  }
  
  &.top-right {
    top: 0;
    right: 0;
    border-top: 2px solid;
    border-right: 2px solid;
  }
  
  &.bottom-left {
    bottom: 0;
    left: 0;
    border-bottom: 2px solid;
    border-left: 2px solid;
  }
  
  &.bottom-right {
    bottom: 0;
    right: 0;
    border-bottom: 2px solid;
    border-right: 2px solid;
  }
`;

const ClientNameDecoration = styled.div`
  position: absolute;
  z-index: 0;
  
  &.left-line {
    left: -20px;
    top: 50%;
    width: 15px;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.8));
    transform: translateY(-50%);
  }
  
  &.right-line {
    right: -20px;
    top: 50%;
    width: 15px;
    height: 2px;
    background: linear-gradient(270deg, transparent, rgba(0, 255, 255, 0.8));
    transform: translateY(-50%);
  }
  
  &.data-point {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(0, 255, 255, 0.8);
    box-shadow: 0 0 10px rgba(0, 255, 255, 0.8);
    animation: ${glowPulse} 2s infinite;
  }
  
  &.data-point-left {
    left: -30px;
    top: 50%;
    transform: translateY(-50%);
  }
  
  &.data-point-right {
    right: -30px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const ClientName = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.2rem;
  font-weight: 700;
  padding: 0.8rem 2.5rem;
  background: linear-gradient(90deg, 
    rgba(0, 150, 255, 0.2), 
    rgba(0, 255, 255, 0.4), 
    rgba(0, 150, 255, 0.2));
  background-size: 200% 100%;
  animation: 
    ${clientGradient} 5s ease infinite, 
    ${textReveal} 1s ease forwards,
    ${glitchEffect} 5s infinite alternate;
  border-radius: 8px;
  position: relative;
  color: #fff;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.7), 0 0 20px rgba(0, 255, 255, 0.5);
  letter-spacing: 2px;
  display: inline-block;
  transform: perspective(500px) translateZ(0px);
  transition: all 0.3s ease;
  overflow: hidden;
  box-shadow: 
    0 0 20px rgba(0, 150, 255, 0.2),
    inset 0 0 15px rgba(0, 150, 255, 0.2);
  border: 1px solid rgba(0, 255, 255, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 10px;
    background: linear-gradient(45deg, #00ffff, #0096ff, #00ffff);
    opacity: 0.5;
    z-index: -1;
    filter: blur(2px);
  }
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    height: 100%;
    width: 100%;
    background: linear-gradient(0deg, 
      transparent 0%, 
      rgba(0, 255, 255, 0.2) 50%, 
      transparent 100%);
    opacity: 0.5;
    animation: ${scanlineAnim} 4s linear infinite;
    z-index: 1;
    pointer-events: none;
  }
  
  &:hover {
    transform: perspective(500px) translateZ(15px);
    letter-spacing: 3px;
    box-shadow: 
      0 0 30px rgba(0, 150, 255, 0.3),
      inset 0 0 20px rgba(0, 150, 255, 0.3);
    text-shadow: 
      0 0 15px rgba(0, 255, 255, 0.8), 
      0 0 30px rgba(0, 255, 255, 0.6);
  }
`;

const StartButton = styled.button`
  position: relative;
  padding: 1.2rem 3.5rem;
  font-family: 'Orbitron', sans-serif;
  font-size: 1.2rem;
  background: rgba(0, 20, 40, 0.8);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: 2rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  animation: ${pulseGlow} 3s infinite;
  
  .text {
    position: relative;
    z-index: 1;
    background: linear-gradient(90deg, #64B6FF, #00ffff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    filter: drop-shadow(0 0 8px rgba(0, 255, 255, 0.5));
    transition: filter 0.3s ease;
  }
  
  .tech-border {
    position: absolute;
    inset: 0;
    border: 2px solid transparent;
    background: linear-gradient(45deg, #64B6FF, #00ffff, #64B6FF) border-box;
    -webkit-mask:
      linear-gradient(#fff 0 0) padding-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0.5;
    transition: all 0.4s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: -2px;
    background: linear-gradient(45deg, #64B6FF20, #00ffff20);
    border-radius: 12px;
    z-index: -2;
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-2px) scale(1.02);
    background: rgba(0, 25, 50, 0.9);
    
    .text {
      filter: drop-shadow(0 0 12px rgba(0, 255, 255, 0.8));
    }
    
    .tech-border {
      opacity: 1;
      border-width: 2.5px;
      background: linear-gradient(45deg, #64B6FF, #00ffff, #64B6FF) border-box;
    }
    
    &::after {
      opacity: 1;
    }
  }
  
  &:active {
    transform: translateY(1px) scale(0.98);
  }
`;

const TechCircle = styled.div`
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  border: 1px solid rgba(100, 182, 255, 0.3);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: -1;
  opacity: 0.5;
  pointer-events: none;
  
  &::before {
    content: '';
    position: absolute;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    border: 1px dashed rgba(0, 255, 255, 0.3);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    animation: ${rotateGlow} 60s linear infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    border: 2px solid rgba(100, 182, 255, 0.2);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    animation: ${rotateGlow} 30s linear infinite reverse;
  }
`;

const DataPoint = styled.div`
  position: absolute;
  width: 4px;
  height: 4px;
  background: rgba(0, 255, 255, 0.8);
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(0, 255, 255, 0.8);
  opacity: 0.7;
  z-index: -1;
  animation: ${glowPulse} 2s infinite;
`;

function BemVindo() {
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [dataPoints, setDataPoints] = useState([]);
  const dataPointsRef = useRef([]);
  
  // Extrair o parâmetro da URL
  const queryParams = new URLSearchParams(location.search);
  const empresaNome = queryParams.get('empresa') || '';
  
  // Generate random data points for tech effect
  useEffect(() => {
    const generateDataPoints = () => {
      const points = [];
      for (let i = 0; i < 30; i++) {
        points.push({
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 5}s`,
          size: `${2 + Math.random() * 4}px`,
        });
      }
      dataPointsRef.current = points;
    };
    
    generateDataPoints();
    setVisible(true);
    
    // Regenerate points periodically for dynamic effect
    const interval = setInterval(() => {
      generateDataPoints();
    }, 15000);
    
    return () => clearInterval(interval);
  }, []);

  const handleStart = () => {
    navigate('/auth');
  };

  return (
    <>
      {/* Enhanced particle background for visual consistency */}
      <ParticlesBackground />
      <BemVindoContainer>
        <TechCircle />
        
        {/* Random data points for tech effect */}
        {dataPointsRef.current.map((point, index) => (
          <DataPoint 
            key={index}
            style={{
              left: point.left,
              top: point.top,
              animationDelay: point.animationDelay,
              width: point.size,
              height: point.size
            }}
          />
        ))}
        
        <h1>Bem-vindo à BIANCA</h1>
        {empresaNome && (
          <ClientNameContainer>
            <ClientNameCorner className="top-left" />
            <ClientNameCorner className="top-right" />
            <ClientNameCorner className="bottom-left" />
            <ClientNameCorner className="bottom-right" />
            <ClientNameDecoration className="left-line" />
            <ClientNameDecoration className="right-line" />
            <ClientNameDecoration className="data-point data-point-left" />
            <ClientNameDecoration className="data-point data-point-right" />
            <ClientName>{empresaNome}</ClientName>
          </ClientNameContainer>
        )}
        <h2>Nesta reunião temos 5 objetivos:</h2>
        
        <ul>
          <li style={{"--index": 0}}>
            <NumberWrapper>01</NumberWrapper>
            Definir os aspectos principais do atendimento da BIANCA
          </li>
          <li style={{"--index": 1}}>
            <NumberWrapper>02</NumberWrapper>
            Criar contas nas plataformas Vapi, Twilio e OpenAI
          </li>
          <li style={{"--index": 2}}>
            <NumberWrapper>03</NumberWrapper>
            Enviar acesso ao Meta e ao seu CRM
          </li>
          <li style={{"--index": 3}}>
            <NumberWrapper>04</NumberWrapper>
            Configurar o primeiro produto na BIANCA
          </li>
          <li style={{"--index": 4}}>
            <NumberWrapper>05</NumberWrapper>
            Agendar a reunião de entrega da implementação
          </li>
        </ul>
        <StartButton onClick={handleStart}>
          <span className="text">Começar</span>
          <div className="tech-border" />
        </StartButton>
      </BemVindoContainer>
    </>
  );
}

export default BemVindo;
